import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { getCountries } from '../../data/locationData';
import { getTimezone } from '../../data/timezones';
import { normalizeTimezoneForFormSelect } from '../../utils/timezoneUtils';
import { toCalendarYmd, formatLocalYMD } from '../../utils/dateTimeUtils';
import TimezoneSelect from '../../components/TimezoneSelect';

const ROLE_LABELS = {
  admin: 'Admin',
  teacher: 'Teacher',
  teacher_supervisor: 'Teacher Supervisor',
  teacher_admin: 'Teacher Admin',
  student: 'Student',
  parent: 'Parent',
  sales: 'Sales',
  sales_supervisor: 'Sales Supervisor',
  country_manager: 'Country Manager',
  parent_feedback_agent: 'Parent Feedback Agent',
  finance_user: 'Finance User',
  finance_supervisor: 'Finance Supervisor',
  finance_admin: 'Finance Admin',
};

const PASSWORD_HINTS = [
  'At least 8 characters',
  'One uppercase letter',
  'One lowercase letter',
  'One number',
  'One special character',
];

function formatRoleLabel(role) {
  if (!role) return '—';
  return ROLE_LABELS[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function buildProfileForm(user) {
  return {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    country: user?.country || '',
    timezone: normalizeTimezoneForFormSelect(user?.timezone),
    dateOfBirth: toCalendarYmd(user?.dateOfBirth),
  };
}

const Profile = () => {
  const { user, updateUser, updateProfile, changePassword } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'password' ? 1 : 0;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [profileForm, setProfileForm] = useState(() => buildProfileForm(user));
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const countries = useMemo(() => getCountries(), []);

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const response = await authAPI.getProfile();
        if (cancelled) return;
        if (response.data?.success && response.data?.user) {
          const profile = response.data.user;
          updateUser(profile);
          setProfileForm(buildProfileForm(profile));
        } else {
          setLoadError('Could not load profile.');
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error.response?.data?.message || 'Could not load profile.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [updateUser]);

  const handleTabChange = (_event, value) => {
    if (value === 1) {
      setSearchParams({ tab: 'password' });
    } else {
      setSearchParams({});
    }
  };

  const handleProfileChange = (field) => (event) => {
    const value = event.target.value;
    setProfileForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'country') {
        next.timezone = getTimezone(value) || prev.timezone || 'UTC';
      }
      return next;
    });
    if (profileErrors[field]) {
      setProfileErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validateProfile = () => {
    const errors = {};
    if (!profileForm.firstName.trim()) errors.firstName = 'First name is required';
    else if (profileForm.firstName.trim().length < 2) errors.firstName = 'At least 2 characters';
    if (!profileForm.lastName.trim()) errors.lastName = 'Last name is required';
    else if (profileForm.lastName.trim().length < 2) errors.lastName = 'At least 2 characters';
    if (!profileForm.country.trim()) errors.country = 'Country is required';
    if (!profileForm.timezone.trim()) errors.timezone = 'Timezone is required';
    if (profileForm.dateOfBirth) {
      if (profileForm.dateOfBirth > formatLocalYMD()) errors.dateOfBirth = 'Date of birth cannot be in the future';
    }
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    if (!validateProfile()) return;

    setProfileSaving(true);
    const result = await updateProfile({
      firstName: profileForm.firstName.trim(),
      lastName: profileForm.lastName.trim(),
      phone: profileForm.phone.trim(),
      country: profileForm.country.trim(),
      timezone: profileForm.timezone.trim(),
      dateOfBirth: profileForm.dateOfBirth || null,
    });
    if (result.success && result.user) {
      setProfileForm(buildProfileForm(result.user));
    }
    setProfileSaving(false);
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordForm((prev) => ({ ...prev, [field]: event.target.value }));
    if (passwordError) setPasswordError('');
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError('');

    if (!passwordForm.currentPassword) {
      setPasswordError('Current password is required');
      return;
    }
    if (!passwordForm.newPassword) {
      setPasswordError('New password is required');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New password and confirmation do not match');
      return;
    }
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setPasswordSaving(true);
    const result = await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
    setPasswordSaving(false);

    if (result.success) {
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } else {
      setPasswordError(result.error || 'Password change failed');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account details and password.
        </Typography>
      </Box>

      {loadError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      ) : null}

      <Paper elevation={1} sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography variant="h6" sx={{ mr: 1 }}>
            {user?.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Account'}
          </Typography>
          <Chip size="small" label={formatRoleLabel(user?.role)} color="primary" variant="outlined" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {user?.email || '—'}
        </Typography>
      </Paper>

      <Paper elevation={1}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Profile details" />
          <Tab label="Change password" />
        </Tabs>

        {activeTab === 0 ? (
          <Box component="form" onSubmit={handleProfileSubmit} sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First name"
                  value={profileForm.firstName}
                  onChange={handleProfileChange('firstName')}
                  error={!!profileErrors.firstName}
                  helperText={profileErrors.firstName}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last name"
                  value={profileForm.lastName}
                  onChange={handleProfileChange('lastName')}
                  error={!!profileErrors.lastName}
                  helperText={profileErrors.lastName}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  value={user?.email || ''}
                  disabled
                  helperText="Email cannot be changed here. Contact support if you need to update it."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profileForm.phone}
                  onChange={handleProfileChange('phone')}
                  placeholder="+91 9876543210"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Country"
                  value={profileForm.country}
                  onChange={handleProfileChange('country')}
                  error={!!profileErrors.country}
                  helperText={profileErrors.country}
                  required
                >
                  {countries.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of birth"
                  type="date"
                  value={profileForm.dateOfBirth}
                  onChange={handleProfileChange('dateOfBirth')}
                  error={!!profileErrors.dateOfBirth}
                  helperText={profileErrors.dateOfBirth || 'Optional. Used for team birthday reminders.'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TimezoneSelect
                  label="Timezone"
                  value={profileForm.timezone}
                  onChange={(next) => handleProfileChange('timezone')({ target: { value: next } })}
                  error={!!profileErrors.timezone}
                  helperText={profileErrors.timezone}
                  required
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" disabled={profileSaving}>
                {profileSaving ? 'Saving...' : 'Save changes'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box component="form" onSubmit={handlePasswordSubmit} sx={{ p: 3 }}>
            {passwordError ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {passwordError}
              </Alert>
            ) : null}

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Current password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange('currentPassword')}
                  autoComplete="current-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowCurrentPassword((prev) => !prev)}
                          edge="end"
                          aria-label="toggle current password visibility"
                        >
                          {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="New password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange('newPassword')}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword((prev) => !prev)}
                          edge="end"
                          aria-label="toggle new password visibility"
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Confirm new password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange('confirmPassword')}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword((prev) => !prev)}
                          edge="end"
                          aria-label="toggle confirm password visibility"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Password requirements:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
              {PASSWORD_HINTS.map((hint) => (
                <Typography component="li" variant="body2" key={hint}>
                  {hint}
                </Typography>
              ))}
            </Box>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" disabled={passwordSaving}>
                {passwordSaving ? 'Updating...' : 'Update password'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Profile;
